<script>
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

const FETCH_INFO_EVENT = 'chatwoot-dashboard-app:fetch-info';
const COMMAND_EVENT = 'chatwoot-dashboard-app:command';
const COMMAND_RESULT_EVENT = 'chatwoot-dashboard-app:command-result';

export default {
  components: {
    LoadingState,
  },
  props: {
    config: {
      type: Array,
      default: () => [],
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      hasOpenedAtleastOnce: false,
      iframeLoading: true,
    };
  },
  computed: {
    dashboardAppContext() {
      return {
        conversation: this.currentChat,
        contact: this.contact,
        currentAgent: this.currentAgent,
      };
    },
    contact() {
      return (
        this.$store.getters['contacts/getContact'](this.contactId) ||
        this.currentChat?.meta?.sender ||
        {}
      );
    },
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    currentAgent() {
      const { id, name, email } = this.$store.getters.getCurrentUser;
      return { id, name, email };
    },
    resolvedConfig() {
      return this.config.map(configItem => ({
        ...configItem,
        resolvedUrl: this.interpolateUrlTemplate(configItem.url),
      }));
    },
  },
  watch: {
    isVisible() {
      if (this.isVisible) {
        this.hasOpenedAtleastOnce = true;
      }
    },
  },
  mounted() {
    window.addEventListener('message', this.triggerEvent);
  },
  unmounted() {
    window.removeEventListener('message', this.triggerEvent);
  },
  methods: {
    normalizeVariableKey(key) {
      return key
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },
    setVariable(variables, key, value) {
      if (!key || value === undefined || value === null || value === '') {
        return;
      }

      const exactKey = key.toString().trim().toLowerCase();
      const normalizedKey = this.normalizeVariableKey(key);

      variables[exactKey] = value;
      if (normalizedKey) {
        variables[normalizedKey] = value;
      }
    },
    appendVariablesFromObject(variables, source) {
      Object.entries(source || {}).forEach(([key, value]) => {
        this.setVariable(variables, key, value);
      });
    },
    buildTemplateVariables() {
      const variables = {};
      const contact = this.contact;
      const conversation = this.currentChat || {};
      const sender = conversation?.meta?.sender || {};
      const assignee = conversation?.meta?.assignee || {};

      this.setVariable(
        variables,
        'telefone',
        contact.phone_number || sender.phone_number
      );
      this.setVariable(
        variables,
        'phone',
        contact.phone_number || sender.phone_number
      );
      this.setVariable(
        variables,
        'phone_number',
        contact.phone_number || sender.phone_number
      );
      this.setVariable(variables, 'nome', contact.name || sender.name);
      this.setVariable(variables, 'name', contact.name || sender.name);
      this.setVariable(variables, 'email', contact.email || sender.email);
      this.setVariable(variables, 'contact_id', contact.id || sender.id);
      this.setVariable(variables, 'conversation_id', conversation.id);
      this.setVariable(
        variables,
        'display_id',
        conversation.display_id || conversation.id
      );
      this.setVariable(variables, 'inbox_id', conversation.inbox_id);
      this.setVariable(variables, 'status', conversation.status);
      this.setVariable(variables, 'priority', conversation.priority);
      this.setVariable(variables, 'assignee_name', assignee.name);
      this.setVariable(variables, 'assignee_email', assignee.email);
      this.setVariable(variables, 'current_agent_name', this.currentAgent.name);
      this.setVariable(
        variables,
        'current_agent_email',
        this.currentAgent.email
      );

      this.appendVariablesFromObject(variables, conversation.custom_attributes);
      this.appendVariablesFromObject(
        variables,
        conversation.additional_attributes
      );
      this.appendVariablesFromObject(variables, contact.custom_attributes);

      return variables;
    },
    interpolateUrlTemplate(url) {
      if (!url) {
        return '';
      }

      const variables = this.buildTemplateVariables();

      return url.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, rawKey) => {
        const lookupKey = rawKey.toLowerCase();
        const normalizedLookupKey = this.normalizeVariableKey(rawKey);
        const value =
          variables[lookupKey] ?? variables[normalizedLookupKey] ?? '';

        return encodeURIComponent(value);
      });
    },
    parseIncomingMessage(data) {
      if (typeof data === 'string') {
        if (data === FETCH_INFO_EVENT) {
          return data;
        }

        try {
          return JSON.parse(data);
        } catch (error) {
          return null;
        }
      }

      return data;
    },
    getFrameElement(index) {
      return document.getElementById(this.getFrameId(index));
    },
    getFrameIndexFromEvent(event) {
      return this.resolvedConfig.findIndex((_, index) => {
        const frameElement = this.getFrameElement(index);
        return frameElement?.contentWindow === event.source;
      });
    },
    async executeAppCommand(command, payload = {}) {
      const conversationId = this.currentChat?.id;

      switch (command) {
        case 'updateConversationCustomAttributes':
          return this.$store.dispatch('updateCustomAttributes', {
            conversationId,
            customAttributes:
              payload.customAttributes || payload.attributes || {},
          });
        case 'updateContact': {
          const contactId = this.contact?.id || this.contactId;
          const updatedContact = await this.$store.dispatch('contacts/update', {
            id: contactId,
            name: payload.name,
            email: payload.email,
            phoneNumber: payload.phoneNumber || payload.phone_number,
            identifier: payload.identifier,
            customAttributes:
              payload.customAttributes || payload.custom_attributes,
          });

          this.$store.commit('UPDATE_CONVERSATION_CONTACT', {
            conversationId,
            ...updatedContact,
          });

          return updatedContact;
        }
        case 'setConversationPriority':
          await this.$store.dispatch('assignPriority', {
            conversationId,
            priority: payload.priority || null,
          });
          return { priority: payload.priority || null };
        case 'setConversationStatus':
          await this.$store.dispatch('toggleStatus', {
            conversationId,
            status: payload.status,
            snoozedUntil: payload.snoozedUntil || null,
          });
          return {
            status: payload.status,
            snoozedUntil: payload.snoozedUntil || null,
          };
        default:
          throw new Error(`Unsupported dashboard app command: ${command}`);
      }
    },
    postMessageToFrame(index, payload) {
      const frameElement = this.getFrameElement(index);
      frameElement?.contentWindow?.postMessage(JSON.stringify(payload), '*');
    },
    async handleCommandMessage(index, message) {
      try {
        const data = await this.executeAppCommand(
          message.command,
          message.payload
        );

        this.postMessageToFrame(index, {
          event: COMMAND_RESULT_EVENT,
          command: message.command,
          status: 'success',
          data,
        });
      } catch (error) {
        this.postMessageToFrame(index, {
          event: COMMAND_RESULT_EVENT,
          command: message.command,
          status: 'error',
          error: error.message,
        });
      }
    },
    triggerEvent(event) {
      if (!this.isVisible) return;
      const index = this.getFrameIndexFromEvent(event);
      if (index < 0) return;

      const message = this.parseIncomingMessage(event.data);

      if (message === FETCH_INFO_EVENT || message?.event === FETCH_INFO_EVENT) {
        this.onIframeLoad(index);
      }

      if (message?.event === COMMAND_EVENT && message.command) {
        this.handleCommandMessage(index, message);
      }
    },
    getFrameId(index) {
      return `dashboard-app--frame-${this.position}-${index}`;
    },
    onIframeLoad(index) {
      // A possible alternative is to use ref instead of document.getElementById
      // However, when ref is used together with v-for, the ref you get will be
      // an array containing the child components mirroring the data source.
      const eventData = { event: 'appContext', data: this.dashboardAppContext };
      this.postMessageToFrame(index, eventData);
      this.iframeLoading = false;
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="hasOpenedAtleastOnce" class="dashboard-app--container">
    <div
      v-for="(configItem, index) in resolvedConfig"
      :key="index"
      class="dashboard-app--list"
    >
      <LoadingState
        v-if="iframeLoading"
        :message="$t('DASHBOARD_APPS.LOADING_MESSAGE')"
        class="dashboard-app_loading-container"
      />
      <iframe
        v-if="configItem.type === 'frame' && configItem.url"
        :id="getFrameId(index)"
        :src="configItem.resolvedUrl"
        @load="() => onIframeLoad(index)"
      />
    </div>
  </div>
</template>

<style scoped>
.dashboard-app--container,
.dashboard-app--list,
.dashboard-app--list iframe {
  height: 100%;
  width: 100%;
}

.dashboard-app--list iframe {
  border: 0;
}
.dashboard-app_loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}
</style>
